import { FC, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Order } from "@module-bindings/types";
import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  assetIdKey,
  formatAssetLabel,
  formatAssetValue,
  formatMarketPairLabel,
} from "@/lib/marketUtils";

type Tab = "orders" | "global";

const FRACTIONAL_BITS = 60n;

/** Convert a u128 fixed-point price (60 fractional bits) to a display string. */
function formatPrice(raw: bigint): string {
  const integer = raw >> FRACTIONAL_BITS;
  const fractional = raw & ((1n << FRACTIONAL_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  // Scale to 18 decimal digits, then trim trailing zeros
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRACTIONAL_BITS) / 2n) >> FRACTIONAL_BITS;
  const fracStr = scaled.toString().padStart(18, "0").replace(/0+$/, "");
  return `${integer}.${fracStr}`;
}

function deriveStatus(order: Order): "open" | "filled" | "partial" {
  if (order.remaining === 0n) return "filled";
  if (order.remaining < order.originalAmount) return "partial";
  return "open";
}

const statusColors: Record<string, string> = {
  open: "text-sr-mint",
  filled: "text-sr-lemon-muted",
  partial: "text-sr-tangerine",
};

import type { Market } from "@module-bindings/types";

interface ActivityPanelProps {
  selectedMarket: Market | null;
}

export const ActivityPanel: FC<ActivityPanelProps> = ({ selectedMarket }) => {
  const [activeTab, setActiveTab] = useState<Tab>("orders");
  const [marketFilter, setMarketFilter] = useState(true); // true = this market only
  const { myAccountId, markets } = useGummiwormContext();
  const [allOrders] = useTable(tables.orders);

  // Selected market's pair keys for filtering
  const selectedBaseKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.base) : null;
  const selectedQuoteKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.quote) : null;

  const isMarketMatch = (order: Order) => {
    if (!selectedBaseKey || !selectedQuoteKey) return true;
    return assetIdKey(order.pair.base) === selectedBaseKey &&
           assetIdKey(order.pair.quote) === selectedQuoteKey;
  };

  const myOrders = useMemo(
    () => myAccountId != null
      ? allOrders.filter((o) => o.owner === myAccountId)
      : [],
    [allOrders, myAccountId],
  );

  const displayOrders = useMemo(() => {
    const base = activeTab === "orders" ? myOrders : allOrders;
    return marketFilter && selectedMarket ? base.filter(isMarketMatch) : base;
  }, [activeTab, myOrders, allOrders, marketFilter, selectedBaseKey, selectedQuoteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort by created descending (newest first)
  const sorted = useMemo(
    () => [...displayOrders].sort((a, b) => Number(b.created - a.created)),
    [displayOrders],
  );

  // Look up market for an order's trading pair
  const marketForOrder = (order: Order) =>
    markets.find((m) => {
      const ob = assetIdKey(order.pair.base);
      const oq = assetIdKey(order.pair.quote);
      const mb = assetIdKey(m.tradingPair.base);
      const mq = assetIdKey(m.tradingPair.quote);
      return ob === mb && oq === mq;
    });

  return (
    <div className="rounded-xl overflow-hidden bg-[#23141A]">
      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-sr-border-subtle px-5">
        <div className="flex">
          {([
            ["orders", "My Orders"],
            ["global", "All Orders"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? "text-sr-lemon-muted border-sr-lemon-muted"
                  : "text-sr-neutral-500 border-transparent hover:text-sr-neutral-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {selectedMarket && (
          <div className="flex items-center gap-1 bg-sr-surface rounded-full p-0.5">
            <button
              onClick={() => setMarketFilter(true)}
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                marketFilter
                  ? "bg-sr-surface-raised text-white"
                  : "text-sr-neutral-500 hover:text-sr-neutral-400"
              }`}
            >
              This market
            </button>
            <button
              onClick={() => setMarketFilter(false)}
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                !marketFilter
                  ? "bg-sr-surface-raised text-white"
                  : "text-sr-neutral-500 hover:text-sr-neutral-400"
              }`}
            >
              All
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] text-sr-neutral-500 uppercase tracking-wider">
              <th className="text-left font-medium px-5 py-3">Pair</th>
              <th className="text-left font-medium px-3 py-3">Side</th>
              <th className="text-left font-medium px-3 py-3">Amount</th>
              <th className="text-left font-medium px-3 py-3">Remaining</th>
              <th className="text-left font-medium px-3 py-3">Price</th>
              <th className="text-left font-medium px-3 py-3">Status</th>
              <th className="text-right font-medium px-5 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((order) => {
              const market = marketForOrder(order);
              const pairLabel = market
                ? formatMarketPairLabel(market)
                : `${formatAssetLabel(order.pair.base)}/${formatAssetLabel(order.pair.quote)}`;
              const status = deriveStatus(order);
              const baseAsset = order.pair.base;
              const isBid = order.side.tag === "Bid";

              return (
                <tr
                  key={order.id.toString()}
                  className="border-t border-sr-border-subtle/50 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-5 py-3">
                    <span className="text-sm text-white font-medium">{pairLabel}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-medium ${isBid ? "text-sr-mint" : "text-sr-salmon"}`}>
                      {isBid ? "Buy" : "Sell"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-sm text-white tabular-nums">
                      {formatAssetValue(baseAsset, order.originalAmount)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-sm text-sr-neutral-300 tabular-nums">
                      {formatAssetValue(baseAsset, order.remaining)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-sm text-white tabular-nums">
                      {formatPrice(order.price)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-medium capitalize ${statusColors[status]}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-sm text-sr-neutral-300 tabular-nums">
                      {order.created > 0n
                        ? new Date(Number(order.created)).toLocaleString()
                        : "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-sm text-sr-neutral-500">
                  {myAccountId == null ? "Connect wallet to view orders" : "No orders yet"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
